import * as THREE from "three";

// Procedural, seamlessly tiling canvas textures for the 3D room (no asset downloads).
const cache = new Map();

const lcg = (seed) => {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
};

const toTexture = (canvas) => {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 16;
  return t;
};

const speckle = (g, w, h, r, amt) => {
  const img = g.getImageData(0, 0, w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (r() - 0.5) * amt;
    d[i] += n;
    d[i + 1] += n;
    d[i + 2] += n;
  }
  g.putImageData(img, 0, 0);
};

// soft < 1 fades the grain contrast: maple is a tight, faint grain next to oak or walnut
export const WOODS = {
  oak: {
    base: "#c8a470",
    dark: "#8f6b3f",
    light: "#e3ca9a",
    seed: 11,
    lines: 220,
    soft: 1,
  },
  walnut: {
    base: "#5b3e2c",
    dark: "#31201a",
    light: "#7f5c43",
    seed: 23,
    lines: 260,
    soft: 1,
  },
  espresso: {
    base: "#38231a",
    dark: "#1c100b",
    light: "#4e3325",
    seed: 37,
    lines: 240,
    soft: 1,
  },
  honeymaple: {
    base: "#d4b48c",
    dark: "#a67d4e",
    light: "#eedcb8",
    seed: 53,
    lines: 170,
    soft: 1,
  },
  maple: {
    base: "#cabca8",
    dark: "#9c8868",
    light: "#e6dccb",
    seed: 61,
    lines: 150,
    soft: 0.85,
  },
};

const rgba = (hex, a) =>
  `rgba(${parseInt(hex.slice(1, 3), 16)},${parseInt(hex.slice(3, 5), 16)},${parseInt(hex.slice(5, 7), 16)},${a})`;

// 24" x 48" of vertical grain; UVs on cabinet parts are in inches so 1 repeat = 24" x 48"
export const woodTexture = (kind) => {
  const key = `wood:${kind}`;
  if (cache.has(key)) return cache.get(key);
  const spec = WOODS[kind] || WOODS.oak;
  const W = 512;
  const H = 1024;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const g = c.getContext("2d");
  const r = lcg(spec.seed);
  const soft = spec.soft ?? 1;
  g.fillStyle = spec.base;
  g.fillRect(0, 0, W, H);
  for (let i = 0; i < 14; i++) {
    const x = r() * W;
    const w = 30 + r() * 120;
    g.globalAlpha = (0.08 + r() * 0.1) * soft;
    g.fillStyle = r() > 0.5 ? spec.dark : spec.light;
    [-W, 0, W].forEach((o) => g.fillRect(x + o, 0, w, H));
  }
  for (let i = 0; i < spec.lines; i++) {
    const x = r() * W;
    const wob = 3 + r() * 14;
    const k = 1 + Math.floor(r() * 3);
    const ph = r() * Math.PI * 2;
    g.globalAlpha = (0.06 + r() * 0.24) * soft;
    g.lineWidth = 0.5 + r() * 2;
    g.strokeStyle = r() > 0.45 ? spec.dark : spec.light;
    [-W, 0, W].forEach((o) => {
      g.beginPath();
      for (let y = 0; y <= H; y += 12)
        g.lineTo(x + o + Math.sin((y / H) * Math.PI * 2 * k + ph) * wob, y);
      g.stroke();
    });
  }
  g.globalAlpha = 1;
  speckle(g, W, H, r, 16 * soft);
  const t = toTexture(c);
  t.repeat.set(1 / 24, 1 / 48);
  cache.set(key, t);
  return t;
};

// 24" x 48" rotary-cut veneer for door and drawer panels: soft curl blooms under nested
// cathedral arches. Mirrored repeat so a panel taller than 48" never shows a hard seam.
export const figuredWoodTexture = (kind) => {
  const key = `figured:${kind}`;
  if (cache.has(key)) return cache.get(key);
  const spec = WOODS[kind] || WOODS.oak;
  const soft = spec.soft ?? 1;
  const W = 512;
  const H = 1024;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const g = c.getContext("2d");
  const r = lcg(spec.seed * 7 + 3);
  g.fillStyle = spec.base;
  g.fillRect(0, 0, W, H);
  for (let i = 0; i < 18; i++) {
    const x = r() * W;
    const y = r() * H;
    const rad = 80 + r() * 220;
    const tone = r() > 0.5 ? spec.light : spec.dark;
    const grad = g.createRadialGradient(x, y, 0, x, y, rad);
    grad.addColorStop(0, rgba(tone, (0.06 + r() * 0.12) * soft));
    grad.addColorStop(1, rgba(tone, 0));
    g.fillStyle = grad;
    g.fillRect(0, 0, W, H);
  }
  const cx = W * (0.35 + r() * 0.3);
  const arches = 70;
  for (let j = 0; j < arches; j++) {
    const apex = -H * 0.35 + (j * H * 1.55) / arches + (r() - 0.5) * 8;
    const spread = W * (0.9 + r() * 0.5);
    const wob = 4 + r() * 10;
    const ph = r() * Math.PI * 2;
    g.globalAlpha = (0.06 + r() * 0.22) * soft;
    g.lineWidth = 0.6 + r() * 1.8;
    g.strokeStyle = r() > 0.4 ? spec.dark : spec.light;
    g.beginPath();
    for (let t = -1; t <= 1.0001; t += 0.02) {
      const x = cx + t * spread + Math.sin(t * 9 + ph) * wob;
      const y = apex + t * t * H * 0.9 + Math.sin((t + 1) * 6 + ph) * wob * 0.6;
      if (t === -1) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke();
  }
  g.globalAlpha = 1;
  speckle(g, W, H, r, 12 * soft);
  const t = toTexture(c);
  t.wrapS = THREE.MirroredRepeatWrapping;
  t.wrapT = THREE.MirroredRepeatWrapping;
  t.repeat.set(1 / 24, 1 / 48);
  cache.set(key, t);
  return t;
};

// 48" x 48": six 8" oak planks with staggered joints
// 48" x 48" of floor boards. "plank" is a warm mid-brown; "oak" is pale white oak with
// wide 7" boards, long lengths and a faint, low-contrast grain.
const FLOOR_WOODS = {
  plank: {
    bg: "#b39672",
    base: "#b89a76",
    dark: "#6b4a2b",
    light: "#d8b78a",
    rows: 6,
    minLen: 0.35,
    maxLen: 0.5,
    gap: "rgba(40,25,12,0.55)",
    grain: 34,
    alpha: 1,
    seed: 77,
  },
  oak: {
    bg: "#d6c7ad",
    base: "#dacbb1",
    dark: "#ab937a",
    light: "#eee4d2",
    rows: 7,
    minLen: 0.5,
    maxLen: 0.45,
    gap: "rgba(95,75,50,0.42)",
    grain: 26,
    alpha: 0.7,
    seed: 131,
  },
};

export const plankFloorTexture = (kind = "plank") => {
  const key = `floor:${kind}`;
  if (cache.has(key)) return cache.get(key);
  const spec = FLOOR_WOODS[kind] || FLOOR_WOODS.plank;
  const S = 1024;
  const c = document.createElement("canvas");
  c.width = S;
  c.height = S;
  const g = c.getContext("2d");
  const r = lcg(spec.seed);
  const rows = spec.rows;
  const ph = S / rows;
  g.fillStyle = spec.bg;
  g.fillRect(0, 0, S, S);
  for (let row = 0; row < rows; row++) {
    let x = -(r() * S * 0.6);
    while (x < S) {
      const len = S * (spec.minLen + r() * spec.maxLen);
      const tone = 0.9 + r() * 0.18;
      const base = new THREE.Color(spec.base).multiplyScalar(tone);
      const grain = Array.from({ length: spec.grain }, () => [
        r() * ph,
        (r() - 0.5) * 6,
        (0.04 + r() * 0.12) * spec.alpha,
        r() > 0.5,
        0.5 + r() * 1.5,
        (r() - 0.5) * ph * 0.6,
      ]);
      [-S, 0, S].forEach((o) => {
        g.globalAlpha = 1;
        g.fillStyle = `#${base.getHexString()}`;
        g.fillRect(x + o, row * ph, len, ph);
        grain.forEach(([gy, drift, a, dark, lw, bow]) => {
          g.globalAlpha = a;
          g.strokeStyle = dark ? spec.dark : spec.light;
          g.lineWidth = lw;
          g.beginPath();
          g.moveTo(x + o, row * ph + gy);
          g.quadraticCurveTo(
            x + o + len / 2,
            row * ph + gy + bow,
            x + o + len,
            row * ph + gy + drift,
          );
          g.stroke();
        });
        g.globalAlpha = 1;
        g.fillStyle = spec.gap;
        g.fillRect(x + o + len - 1.5, row * ph, 3, ph);
      });
      x += len;
    }
    g.fillStyle = spec.gap;
    g.fillRect(0, row * ph - 1, S, 2);
  }
  speckle(g, S, S, r, 10 * spec.alpha);
  const t = toTexture(c);
  cache.set(key, t);
  return t;
};

// 48" x 48": four 24" charcoal porcelain tiles
export const tileFloorTexture = () => {
  const key = "floor:tile";
  if (cache.has(key)) return cache.get(key);
  const S = 1024;
  const c = document.createElement("canvas");
  c.width = S;
  c.height = S;
  const g = c.getContext("2d");
  const r = lcg(91);
  const half = S / 2;
  [
    [0, 0],
    [half, 0],
    [0, half],
    [half, half],
  ].forEach(([x, y]) => {
    g.fillStyle = `#${new THREE.Color("#2c2c32").multiplyScalar(0.9 + r() * 0.2).getHexString()}`;
    g.fillRect(x, y, half, half);
    for (let i = 0; i < 6; i++) {
      const rad = 60 + r() * 160;
      const gx = x + r() * half;
      const gy = y + r() * half;
      const grad = g.createRadialGradient(gx, gy, 0, gx, gy, rad);
      grad.addColorStop(0, `rgba(255,255,255,${0.02 + r() * 0.05})`);
      grad.addColorStop(1, "rgba(255,255,255,0)");
      g.fillStyle = grad;
      g.fillRect(x, y, half, half);
    }
  });
  g.fillStyle = "#17171b";
  [0, half].forEach((p) => {
    g.fillRect(p - 2, 0, 4, S);
    g.fillRect(0, p - 2, S, 4);
  });
  g.fillRect(S - 2, 0, 2, S);
  g.fillRect(0, S - 2, S, 2);
  speckle(g, S, S, r, 8);
  const t = toTexture(c);
  cache.set(key, t);
  return t;
};
